/**
 * Attributes to DECLARE on a part, as opposed to values to state.
 *
 * The distinction is the whole component and it is easy to miss. Every other
 * control on the wizard states a VALUE for an attribute the model already
 * declares; each row here CREATES one. A quantity BEST has never heard of - the
 * seeker's dewar and its cooling power - had to be typed into the `.sysml` file
 * by hand before this existed, because presentation, rollup and budgets all work
 * off a declaration in the model and nothing could write one.
 *
 * A row is a NUMBER or a CHOICE, and the two ask different questions. A
 * quantity needs its kind, which supplies the unit. A choice needs its values,
 * and BEST writes an `enum def` for them - which is the whole difference
 * between a picker and a text box. A thruster's fuel renders as free text today
 * for want of exactly that declaration: the library states the line to write
 * and no program has written it.
 *
 * THERE IS NO UNIT FIELD, and that is deliberate rather than missing. A
 * quantity's unit is a fact about its KIND - power is written in watts here,
 * pressure in psi - so choosing `Power` chooses the unit, and the options come
 * from the same table that units a declared attribute. A form that let somebody
 * type a unit would let them type one BEST would then refuse to read, and the
 * refusal would arrive after the typing.
 *
 * There is no VALUE field either, for a duller reason: the field does not exist
 * until the declaration does, so a value is stated on the edit that follows.
 *
 * What it assembles and refuses lives in `part-wizard-model.ts`, pure and tested
 * directly - "two rows with one name is refused" is a claim about a value rather
 * than about a form.
 */

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import HelpTip from '../common/HelpTip'
import { LINK_KINDS, emptyAttributeDraft } from './part-wizard-model'
import type { AttributeDraft } from './part-wizard-model'
import type { AttributeTypeChoice } from '../../schema'

export const NEW_ATTRIBUTES_LABEL = 'Attributes to declare'

export const NEW_ATTRIBUTES_NOTE =
  'An attribute this part carries that the profile does not already declare — a ' +
  'cooling power, a hold time. Its unit comes from the kind you choose, its ' +
  'description becomes the help beside it, and a value is stated once the part ' +
  'exists.'

export const ROLLS_UP_NOTE =
  'Total this over everything below the part as well as on it, the way dry mass ' +
  'is totalled. Leave it off for a quantity that does not add up — a temperature ' +
  'limit is not the sum of its parts’, and a wrong marker produces a confident ' +
  'number rather than a missing one.'

interface Props {
  rows: readonly AttributeDraft[]
  choices: readonly AttributeTypeChoice[]
  onChange: (rows: AttributeDraft[]) => void
  disabled?: boolean
  error?: string
  /**
   * Offer the '@RollsUp' column. On for a PART, where it is the whole point -
   * dry mass totals over everything below.
   *
   * Off for a requirement type, where nothing rolls up: a requirement is not a
   * subtree of requirements whose masses add. A toggle that reaches a server
   * which ignores it is worse than no toggle - it invites someone to set it and
   * believe something happened.
   */
  showRollsUp?: boolean
  /**
   * Offer "A link" (issue #48): a requirement type's requirements link to an
   * interface, a state, a port, a part or a function. Off for a part, which is
   * what satisfies a requirement and declares no links of its own - the server
   * refuses one there.
   */
  showLinks?: boolean
}

export default function NewAttributesTable({
  rows,
  choices,
  onChange,
  disabled = false,
  error = '',
  showRollsUp = true,
  showLinks = false,
}: Props) {
  const set = (key: string, patch: Partial<AttributeDraft>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', mb: 0.5 }}>
        <Typography variant="subtitle2">{NEW_ATTRIBUTES_LABEL}</Typography>
        <HelpTip label="What this declares" title={NEW_ATTRIBUTES_NOTE} />
      </Stack>

      <Table size="small" aria-label={NEW_ATTRIBUTES_LABEL}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: 0 }}>Name</TableCell>
            <TableCell>Holds</TableCell>
            <TableCell>Kind or values</TableCell>
            {/* 'Help text', not 'Description': the part has a Description of
                its own on this same form, and two fields under one word is a
                question about which one somebody is filling in. */}
            <TableCell>Help text</TableCell>
            {showRollsUp && <TableCell align="center">Rolls up</TableCell>}
            <TableCell padding="none" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const chosen = choices.find((choice) => choice.name === row.declaredType) ?? null
            return (
              <TableRow key={row.key}>
                <TableCell sx={{ pl: 0, verticalAlign: 'top' }}>
                  <TextField
                    size="small"
                    value={row.name}
                    onChange={(event) => set(row.key, { name: event.target.value })}
                    disabled={disabled}
                    placeholder="coolingPower"
                    slotProps={{ htmlInput: { 'aria-label': 'Attribute name' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', minWidth: 130 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={row.shape}
                    onChange={(event) =>
                      set(row.key, {
                        shape: event.target.value as AttributeDraft['shape'],
                      })
                    }
                    disabled={disabled}
                    slotProps={{ htmlInput: { 'aria-label': 'What it holds' } }}
                  >
                    <MenuItem value="quantity">A number</MenuItem>
                    <MenuItem value="choice">A choice</MenuItem>
                    {showLinks && <MenuItem value="link">A link</MenuItem>}
                  </TextField>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', minWidth: 200 }}>
                  {row.shape === 'link' ? (
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={row.linkKind}
                      onChange={(event) => set(row.key, { linkKind: event.target.value })}
                      disabled={disabled}
                      helperText="what its requirements are satisfied by"
                      slotProps={{ htmlInput: { 'aria-label': 'Links to' } }}
                    >
                      {LINK_KINDS.map((kind) => (
                        <MenuItem key={kind.value} value={kind.value}>
                          {kind.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : row.shape === 'choice' ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={row.values}
                      onChange={(event) => set(row.key, { values: event.target.value })}
                      disabled={disabled}
                      placeholder="bare, diaphragm, piston, baffled"
                      // Commas or newlines: a short list is typed on one line
                      // and a long one is pasted from somewhere else.
                      helperText="separated by commas"
                      slotProps={{ htmlInput: { 'aria-label': 'Values' } }}
                    />
                  ) : (
                  <Autocomplete
                    size="small"
                    options={[...choices]}
                    value={chosen}
                    getOptionLabel={(choice) => choice.label}
                    isOptionEqualToValue={(a, b) => a.name === b.name}
                    onChange={(_event, choice) =>
                      set(row.key, { declaredType: choice ? choice.name : '' })
                    }
                    disabled={disabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        // The unit as helper text, because it is the consequence
                        // of the choice and the reason there is no unit field.
                        // 'no unit' rather than blank for a ratio: a genuinely
                        // dimensionless quantity is not one nobody has stated.
                        helperText={
                          chosen
                            ? chosen.text
                              ? 'free text'
                              : chosen.dimensionless
                              ? 'no unit'
                              : chosen.unit.replace(/^.*::/, '').replace(/'/g, '')
                            : ' '
                        }
                        // Every slot from params must survive the merge:
                        // htmlInput carries the ref for the real <input>, and
                        // replacing the object leaves MUI unable to find it.
                        slotProps={{
                          ...params.slotProps,
                          htmlInput: {
                            ...params.slotProps.htmlInput,
                            'aria-label': 'Quantity kind',
                          },
                        }}
                      />
                    )}
                  />
                  )}
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.description}
                    onChange={(event) => set(row.key, { description: event.target.value })}
                    disabled={disabled}
                    placeholder="What it means"
                    slotProps={{ htmlInput: { 'aria-label': 'Attribute help text' } }}
                  />
                </TableCell>
                {showRollsUp && (
                <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={row.rollsUp}
                        onChange={(event) => set(row.key, { rollsUp: event.target.checked })}
                        disabled={disabled}
                        slotProps={{ input: { 'aria-label': 'Rolls up' } }}
                      />
                    }
                    label={<HelpTip label="What rolling up means" title={ROLLS_UP_NOTE} />}
                  />
                </TableCell>
                )}
                <TableCell padding="none" sx={{ verticalAlign: 'top' }}>
                  <IconButton
                    size="small"
                    aria-label="Remove attribute"
                    disabled={disabled || rows.length <= 1}
                    onClick={() => onChange(rows.filter((item) => item.key !== row.key))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <Stack direction="row" sx={{ alignItems: 'center', mt: 1 }} spacing={2}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={disabled}
          onClick={() =>
            onChange([...rows, emptyAttributeDraft(`attr-${rows.length}-${Date.now()}`)])
          }
        >
          Add attribute
        </Button>
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}
